// ============================================================================
// AXIOM — BigNum
// ----------------------------------------------------------------------------
// Mantissa/exponent float with *immutable value semantics*. Everything the game
// inflates (damage, HP, gold, score) is a BigNum.
//
// Invariants held after every construction:
//   * `1 <= |m| < 10`  OR  `m === 0` (in which case `e === 0`)
//   * `e` is a finite JS number in [-MAX_EXP, MAX_EXP]
//   * NEVER NaN and NEVER Infinity unless you explicitly asked for BigNum.NAN
//     (the §4.4 "Undefined Zone" mechanic is the only sanctioned NaN source).
//
// Everything above ~1e308 lives in log space: we only ever touch `e` as a JS
// number, so exponents of 1e6..1e300 are exact enough and cheap. Past MAX_EXP
// we saturate rather than overflow — a saturated BigNum is still a usable
// number, an Infinity is a landmine three modules downstream.
// ============================================================================

/** Largest representable decimal exponent. Beyond this we saturate. */
export const MAX_EXP = 1e300;
/** Below this magnitude a value collapses to exact zero. */
export const MIN_EXP = -1e300;
/** Decades past which `a + b` is just `a` (doubles carry ~15.95 digits). */
const ADD_EPS_DECADES = 17;

const LOG10E = Math.LOG10E;          // 0.4342944819032518
const LN10 = Math.LN10;              // 2.302585092994046
const LOG10_2PI = Math.log10(2 * Math.PI);

// Lanczos g=7, n=9 — good to ~15 significant digits over the whole positive axis.
const LANCZOS = [
  0.99999999999980993, 676.5203681218851, -1259.1392167224028,
  771.32342877765313, -176.61502916214059, 12.507343278686905,
  -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
];

/** log_e Gamma(z) for z > 0 (reflection for z < 0.5). Never returns NaN for finite z. */
export function logGamma(z) {
  if (!Number.isFinite(z)) return 0;
  if (z < 0.5) {
    const s = Math.sin(Math.PI * z);
    if (s === 0) return 0;                       // pole guard
    return Math.log(Math.PI / Math.abs(s)) - logGamma(1 - z);
  }
  const w = z - 1;
  let x = LANCZOS[0];
  for (let i = 1; i < 9; i++) x += LANCZOS[i] / (w + i);
  const t = w + 7.5;
  const r = 0.5 * Math.log(2 * Math.PI) + (w + 0.5) * Math.log(t) - t + Math.log(x);
  return Number.isFinite(r) ? r : 0;
}

/** log10 Gamma(z + 1) — i.e. log10(z!). */
export function logGamma10(z) { return logGamma(z + 1) * LOG10E; }

/** 10^x that degrades gracefully instead of returning Infinity. */
function pow10Safe(x) {
  if (x > 308) return Number.MAX_VALUE;
  if (x < -323) return 0;
  return Math.pow(10, x);
}

function clampExp(e) {
  if (!Number.isFinite(e)) return e > 0 ? MAX_EXP : MIN_EXP;
  if (e > MAX_EXP) return MAX_EXP;
  if (e < MIN_EXP) return MIN_EXP;
  return e;
}

export class BigNum {
  /**
   * @param {number} mantissa  any real; normalized on construct
   * @param {number} exponent  decimal exponent
   */
  constructor(mantissa = 0, exponent = 0) {
    let m = +mantissa;
    let e = +exponent;

    if (Number.isNaN(m) || Number.isNaN(e)) { this.m = NaN; this.e = 0; return; }
    if (m === 0) { this.m = 0; this.e = 0; return; }

    if (!Number.isFinite(m)) { m = m > 0 ? 1 : -1; e = MAX_EXP; }
    if (!Number.isFinite(e)) e = e > 0 ? MAX_EXP : MIN_EXP;

    let a = Math.abs(m);
    if (a < 1 || a >= 10) {
      // Chunked so denormals / 1e-320 style inputs can't blow up the division.
      let d = Math.floor(Math.log10(a));
      if (!Number.isFinite(d)) { this.m = 0; this.e = 0; return; }
      while (d > 150) { m /= 1e150; e += 150; d -= 150; }
      while (d < -150) { m *= 1e150; e -= 150; d += 150; }
      if (d !== 0) { m /= Math.pow(10, d); e += d; }
      // FP fixup — at most one step in either direction.
      while (Math.abs(m) >= 10) { m /= 10; e += 1; }
      while (m !== 0 && Math.abs(m) < 1) { m *= 10; e -= 1; }
    }

    if (e > MAX_EXP) { this.m = m < 0 ? -9.999999999999998 : 9.999999999999998; this.e = MAX_EXP; return; }
    if (e < MIN_EXP) { this.m = 0; this.e = 0; return; }

    this.m = m;
    this.e = e;
  }

  // ---------------------------------------------------------------- factories

  /** Coerce a JS number | numeric string | BigNum into a BigNum. Never throws. */
  static from(n) {
    if (n instanceof BigNum) return n;
    if (n == null) return BigNum.ZERO;
    if (typeof n === 'object' && typeof n.m === 'number' && typeof n.e === 'number') {
      return new BigNum(n.m, n.e);
    }
    const v = typeof n === 'number' ? n : Number(n);
    if (Number.isNaN(v)) return BigNum.ZERO;
    return new BigNum(v, 0);
  }

  /** 10^exp. */
  static fromExp(exp) { return BigNum.fromLog10(exp); }

  /** Build directly from a decimal logarithm (the hot path for every big op). */
  static fromLog10(L, sign = 1) {
    if (Number.isNaN(L)) return BigNum.NAN;
    if (!Number.isFinite(L)) L = L > 0 ? MAX_EXP : MIN_EXP;
    if (L > MAX_EXP) L = MAX_EXP;
    if (L < MIN_EXP) return BigNum.ZERO;
    const e = Math.floor(L);
    let m = Math.pow(10, L - e);
    if (!(m >= 1)) m = 1;
    if (m >= 10) m = 9.999999999999998;
    return new BigNum(sign < 0 ? -m : m, e);
  }

  static max(a, b) { a = BigNum.from(a); b = BigNum.from(b); return a.gte(b) ? a : b; }
  static min(a, b) { a = BigNum.from(a); b = BigNum.from(b); return a.lte(b) ? a : b; }

  // ---------------------------------------------------------------- predicates

  isZero() { return this.m === 0; }
  isNaN() { return Number.isNaN(this.m); }
  isNeg() { return this.m < 0; }
  isPos() { return this.m > 0; }
  /** True for every value this class considers usable (saturated counts as usable). */
  isFinite() { return Number.isFinite(this.m) && Number.isFinite(this.e); }
  /** True when the value has been pinned at the representable ceiling. */
  isSaturated() { return this.e >= MAX_EXP; }

  clone() { return new BigNum(this.m, this.e); }

  // ---------------------------------------------------------------- arithmetic

  add(o) {
    o = BigNum.from(o);
    if (this.isNaN() || o.isNaN()) return BigNum.NAN;
    if (this.m === 0) return o;
    if (o.m === 0) return this;

    // Work off the larger magnitude so tiny terms can't perturb the exponent.
    let big = this, small = o;
    if (o.e > this.e || (o.e === this.e && Math.abs(o.m) > Math.abs(this.m))) { big = o; small = this; }

    const d = big.e - small.e;
    if (d > ADD_EPS_DECADES) return big;              // small is below the last bit

    const m = big.m + small.m * Math.pow(10, -d);
    if (m === 0) return BigNum.ZERO;                   // exact cancellation
    return new BigNum(m, big.e);
  }

  sub(o) { return this.add(BigNum.from(o).neg()); }

  mul(o) {
    o = BigNum.from(o);
    if (this.isNaN() || o.isNaN()) return BigNum.NAN;
    if (this.m === 0 || o.m === 0) return BigNum.ZERO;
    return new BigNum(this.m * o.m, clampExp(this.e + o.e));
  }

  /**
   * Division. Guarded:
   *   x/0 (x != 0) -> saturated value carrying x's sign (a "divergence"),
   *   0/0          -> BigNum.NAN — the sanctioned NaN source (DESIGN §4.4).
   * Ops.div flags `ctx.meta.divZero` before this ever matters to gameplay.
   */
  div(o) {
    o = BigNum.from(o);
    if (this.isNaN() || o.isNaN()) return BigNum.NAN;
    if (o.m === 0) {
      if (this.m === 0) return BigNum.NAN;
      return new BigNum(this.m < 0 ? -1 : 1, MAX_EXP);
    }
    if (this.m === 0) return BigNum.ZERO;
    return new BigNum(this.m / o.m, clampExp(this.e - o.e));
  }

  neg() { return this.m === 0 ? BigNum.ZERO : new BigNum(-this.m, this.e); }
  abs() { return this.m < 0 ? new BigNum(-this.m, this.e) : this; }

  /**
   * x^n with n a JS number (may be fractional, negative, or 1e6+).
   * Negative bases: exact sign for integer n, |x|^n otherwise (the caller —
   * Ops.root / Ops.pow — is responsible for raising `meta.imaginary`).
   */
  pow(n) {
    if (this.isNaN()) return BigNum.NAN;
    let k = Number(n);
    if (!Number.isFinite(k)) k = k > 0 ? MAX_EXP : -MAX_EXP;
    if (k === 0) return BigNum.ONE;
    if (k === 1) return this;
    if (this.m === 0) return k > 0 ? BigNum.ZERO : new BigNum(1, MAX_EXP);

    let sign = 1;
    if (this.m < 0) sign = (Number.isInteger(k) && Math.abs(k % 2) === 1) ? -1 : 1;

    const L = this.logAbs10();
    let L2 = L * k;
    if (!Number.isFinite(L2)) L2 = (L > 0) === (k > 0) ? MAX_EXP : MIN_EXP;
    return BigNum.fromLog10(L2, sign);
  }

  /** n-th root. `root(2)` is sqrt. */
  root(n) {
    const k = Number(n);
    if (!Number.isFinite(k) || k === 0) return this;
    return this.pow(1 / k);
  }

  sqrt() {
    if (this.isNaN()) return BigNum.NAN;
    if (this.m === 0) return BigNum.ZERO;
    return this.abs().pow(0.5);          // sign discarded on purpose; see Ops.root
  }

  /** log10 of |x| as a plain JS number. Zero maps to -324 (the double floor), not -Infinity. */
  logAbs10() {
    if (this.isNaN()) return NaN;
    if (this.m === 0) return -324;
    return this.e + Math.log10(Math.abs(this.m));
  }

  /** Alias kept because "give me the exponent as a number" is the single most-used query. */
  log10Num() { return this.logAbs10(); }

  /** log10 as a BigNum (matches the ARCHITECTURE signature block). */
  log10() { return BigNum.from(this.logAbs10()); }

  /** Natural log as a BigNum. */
  ln() { return BigNum.from(this.logAbs10() * LN10); }

  /** log base b as a BigNum. */
  logBase(b) {
    const lb = Math.log10(Number(b));
    if (!Number.isFinite(lb) || lb === 0) return BigNum.ZERO;
    return BigNum.from(this.logAbs10() / lb);
  }

  /**
   * Tetration x↑↑n, evaluated entirely in log-log space.
   *   log10(x↑↑(k+1)) = 10^log10(x↑↑k) * log10(x)
   * Saturates within 3–4 iterations for any interesting x, which is exactly why
   * it is safe: we never materialise the tower, only its logarithm.
   * Fractional n is interpolated geometrically between the neighbouring heights
   * so the function stays continuous and monotone.
   */
  tetrate(n) {
    if (this.isNaN()) return BigNum.NAN;
    let h = Number(n);
    if (!Number.isFinite(h)) h = 0;
    if (h <= 0) return BigNum.ONE;
    if (this.lte(BigNum.ONE)) return this;            // <=1 tetration is degenerate; pass through
    if (h === 1) return this;

    const L0 = this.logAbs10();
    const steps = Math.floor(h);
    const frac = h - steps;

    let L = L0;
    for (let k = 1; k < steps; k++) {
      L = tetraStep(L, L0);
      if (L >= MAX_EXP) { L = MAX_EXP; break; }
    }
    if (frac > 0 && L < MAX_EXP) {
      const Lnext = tetraStep(L, L0);
      const base = Math.max(L, 1e-12);
      const ratio = Math.max(Lnext, base) / base;
      L = Math.min(L * Math.pow(ratio, frac), MAX_EXP);
    }
    return BigNum.fromLog10(L);
  }

  /**
   * Gamma(x + 1). Lanczos below 1e300, Stirling in pure log space above.
   * This is the mathematically honest factorial — the `!` *token* in Ops.js
   * deliberately uses a tamed variant (see OPS.fact) so the balance curve holds.
   */
  factorial() {
    if (this.isNaN()) return BigNum.NAN;
    if (this.m <= 0) return BigNum.ONE;                // poles / negatives -> identity
    const L = this.logAbs10();
    if (L < 300) {
      const x = this.toNumber();
      return BigNum.fromLog10(logGamma10(x));
    }
    // log10 G(x+1) ~ x*(log10 x - log10 e) + 0.5*log10(2*pi*x)
    const main = this.mul(BigNum.from(L - LOG10E));
    const lg = main.toNumber() + 0.5 * (LOG10_2PI + L);
    return BigNum.fromLog10(Number.isFinite(lg) ? lg : MAX_EXP);
  }

  /** Truncate toward zero. No-op once the value exceeds double integer precision. */
  floor() {
    if (this.isNaN() || this.m === 0) return this;
    if (this.e >= 16) return this;
    const v = this.toNumber();
    return BigNum.from(Math.floor(v));
  }

  // --------------------------------------------------------------- comparisons

  cmp(o) {
    o = BigNum.from(o);
    if (this.isNaN() || o.isNaN()) return 0;
    if (this.m === 0 && o.m === 0) return 0;
    if (this.m === 0) return o.m > 0 ? -1 : 1;
    if (o.m === 0) return this.m > 0 ? 1 : -1;
    const posA = this.m > 0, posB = o.m > 0;
    if (posA !== posB) return posA ? 1 : -1;
    if (this.e !== o.e) return (this.e > o.e) === posA ? 1 : -1;
    if (this.m !== o.m) return this.m > o.m ? 1 : -1;
    return 0;
  }

  gt(o) { return this.cmp(o) > 0; }
  lt(o) { return this.cmp(o) < 0; }
  gte(o) { return this.cmp(o) >= 0; }
  lte(o) { return this.cmp(o) <= 0; }
  eq(o) { return this.cmp(o) === 0; }

  /** Relative equality, for tests and "close enough" gameplay checks. */
  approxEq(o, relTol = 1e-9) {
    o = BigNum.from(o);
    if (this.isNaN() || o.isNaN()) return false;
    if (this.m === 0 || o.m === 0) return this.m === o.m;
    if (this.isNeg() !== o.isNeg()) return false;
    const d = Math.abs(this.logAbs10() - o.logAbs10());
    return d < Math.log10(1 + relTol) + 1e-12 || d < relTol;
  }

  // --------------------------------------------------------------- conversion

  /** May be +/-Infinity for saturated values — that is the documented contract. */
  toNumber() {
    if (this.isNaN()) return NaN;
    if (this.m === 0) return 0;
    if (this.e > 308) return this.m > 0 ? Infinity : -Infinity;
    if (this.e < -324) return 0;
    return this.m * pow10Safe(this.e);
  }

  valueOf() { return this.toNumber(); }
  toString() { return this.formatShort(); }
  [Symbol.toPrimitive](hint) { return hint === 'number' ? this.toNumber() : this.formatShort(); }
  toJSON() { return { m: this.m, e: this.e }; }

  // --------------------------------------------------------------- formatting

  /**
   * @param {'auto'|'sci'|'eng'|'named'|'tetra'|'raw'} mode
   * `auto` tiers (DESIGN §3):
   *   <1e6   -> 1,234,567
   *   <1e36  -> 1.23 Dc   (named suffixes, K..Dc in auto; the table runs to Vg)
   *   <1e1e6 -> 1.23e45
   *   beyond -> 2.48↑↑4   (power-tower notation: `top ↑↑ height`)
   */
  format(mode = 'auto') {
    if (this.isNaN()) return 'NaN';
    if (this.m === 0) return '0';
    const sign = this.m < 0 ? '-' : '';
    const a = this.abs();

    switch (mode) {
      case 'sci': return sign + fmtSci(a);
      case 'eng': return sign + fmtEng(a);
      case 'named': return sign + fmtNamed(a);
      case 'tetra': return sign + fmtTetra(a);
      case 'raw': return sign + a.m.toPrecision(6) + 'e' + a.e;
      default: break;
    }

    if (a.e < 0) return sign + fmtTiny(a);
    if (a.e < 6) return sign + fmtGrouped(a);
    if (a.e < NAMED_MAX_EXP) return sign + fmtNamed(a);
    if (a.e < 1e6) return sign + fmtSci(a);
    return sign + fmtTetra(a);
  }

  /**
   * Compact form for floating damage numbers — never wider than ~8 glyphs so it
   * fits on screen with 200 of its friends.
   */
  formatShort() {
    if (this.isNaN()) return 'NaN';
    if (this.m === 0) return '0';
    const sign = this.m < 0 ? '-' : '';
    const a = this.abs();
    if (a.e < 0) return sign + fmtTiny(a);
    if (a.e < 4) return sign + String(Math.round(a.toNumber()));
    if (a.e < NAMED_MAX_EXP) return sign + fmtNamed(a, true);
    // Past 1e1e5 the exponent alone would eat the whole label, so go to towers early.
    if (a.e < 1e5) return sign + a.m.toFixed(1) + 'e' + a.e;
    return sign + fmtTetra(a);
  }

  /** Order-of-magnitude bucket, for UI colour/scale tiers. */
  tier() {
    const L = this.logAbs10();
    if (!Number.isFinite(L)) return 0;
    return Math.max(0, Math.floor(L / 3));
  }
}

function tetraStep(L, L0) {
  if (L > 300) return MAX_EXP;
  const v = Math.pow(10, L) * L0;
  return Number.isFinite(v) ? Math.min(v, MAX_EXP) : MAX_EXP;
}

// --------------------------------------------------------------- format helpers

/** Full suffix ladder (index i -> 10^(3i+3)). Auto mode stops at Dc; 'named' uses it all. */
export const SUFFIXES = [
  'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc',
  'Ud', 'Dd', 'Td', 'Qad', 'Qid', 'Sxd', 'Spd', 'Ocd', 'Nod', 'Vg',
];
/** Auto switches to scientific here — DESIGN says the named tier ends at ~1e33 (Dc). */
const NAMED_MAX_EXP = 36;

function fmtGrouped(a) {
  const v = Math.round(a.toNumber());
  return v.toLocaleString('en-US');
}

function fmtTiny(a) {
  const v = a.toNumber();
  if (v === 0) return '0';
  if (v >= 0.001) return v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return fmtSci(a);
}

function fmtNamed(a, short = false) {
  const e = a.e;
  if (e < 3) {
    const v = a.toNumber();
    return short ? String(Math.round(v)) : v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  let idx = Math.floor(e / 3) - 1;
  if (idx >= SUFFIXES.length) return fmtSci(a);
  const scaled = a.m * Math.pow(10, e - 3 * (idx + 1));
  const d = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
  return scaled.toFixed(short && scaled >= 100 ? 0 : d) + (short ? '' : ' ') + SUFFIXES[idx];
}

function fmtSci(a) {
  return a.m.toFixed(2) + 'e' + (Number.isInteger(a.e) ? a.e : a.e.toPrecision(6));
}

function fmtEng(a) {
  const e3 = Math.floor(a.e / 3) * 3;
  const scaled = a.m * Math.pow(10, a.e - e3);
  return scaled.toFixed(2) + 'e' + e3;
}

function fmtTetra(a) {
  let L = a.logAbs10();
  let h = 1;                       // value == 10^L, so the tower already has one level
  while (L > 10 && h < 64) { L = Math.log10(L); h++; }
  return L.toFixed(2) + '↑↑' + (h + 1);
}

// ------------------------------------------------------------------- constants

BigNum.ZERO = new BigNum(0, 0);
BigNum.ONE = new BigNum(1, 0);
BigNum.TWO = new BigNum(2, 0);
BigNum.TEN = new BigNum(1, 1);
BigNum.NAN = new BigNum(NaN, 0);
BigNum.MAX = new BigNum(9.999999999999998, MAX_EXP);

export default BigNum;
